import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  FormInsightsJobRolesDocument,
  FormInsightsJobTitlesDocument,
  FormInsightsLegalEntitiesDocument,
  FormInsightsManagersDocument,
  FormInsightsTeamsDocument,
  FormInsightsWorkplacesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

export function useFormInsightsFilters() {
  const { company } = useCurrentSession()
  const client = useApolloClient()

  const defaultFilters = useMemo(
    () => ({
      teamIds: {
        type: 'in' as const,
        label: i18n.t('forms.responses.filters.team'),
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: FormInsightsTeamsDocument,
              fetchPolicy: 'cache-first',
            })
            return (
              result.data?.teams?.teams?.map((team: { name: string; id: string }) => ({
                label: team.name,
                value: team.id,
              })) || []
            )
          },
        },
      },
      locationIds: {
        type: 'in' as const,
        label: i18n.t('forms.responses.filters.workplace'),
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: FormInsightsWorkplacesDocument,
              fetchPolicy: 'cache-first',
            })
            return (
              result.data?.locations?.locations?.map((location: { name: string; id: string }) => ({
                label: location.name,
                value: location.id,
              })) || []
            )
          },
        },
      },
      managerIds: {
        type: 'in' as const,
        label: i18n.t('forms.responses.filters.manager'),
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: FormInsightsManagersDocument,
              fetchPolicy: 'cache-first',
              variables: { companyId: String(company?.id) },
            })
            return (
              result.data?.employees?.employees?.map(
                (employee: { fullName: string; id: string }) => ({
                  label: employee.fullName,
                  value: employee.id,
                })
              ) || []
            )
          },
        },
      },
      legalEntityIds: {
        type: 'in' as const,
        label: i18n.t('forms.responses.filters.legalEntity'),
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: FormInsightsLegalEntitiesDocument,
              fetchPolicy: 'cache-first',
            })
            return (
              result.data?.companies?.legalEntities?.map(
                (entity: { legalName: string; id: string }) => ({
                  label: entity.legalName,
                  value: entity.id,
                })
              ) || []
            )
          },
        },
      },
      jobRoles: {
        type: 'in' as const,
        label: i18n.t('forms.responses.filters.jobRole'),
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: FormInsightsJobRolesDocument,
              fetchPolicy: 'cache-first',
            })
            return (
              result.data?.jobCatalog?.roles?.map((role: { name: string; id: string }) => ({
                label: role.name,
                value: role.id,
              })) || []
            )
          },
        },
      },
      jobTitles: {
        type: 'in' as const,
        label: i18n.t('forms.responses.filters.jobTitle'),
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: FormInsightsJobTitlesDocument,
              fetchPolicy: 'cache-first',
            })
            const contractVersions = result.data?.contracts?.contractVersions || []
            const uniqueJobTitles = Array.from(
              new Set(
                contractVersions
                  .map((contract: { jobTitle?: string | null }) => contract.jobTitle)
                  .filter((title): title is string => Boolean(title))
              )
            )
            return uniqueJobTitles.map((title: string) => ({
              label: title,
              value: title,
            }))
          },
        },
      },
    }),
    [client, company?.id]
  )

  return defaultFilters
}
